import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { ChevronDown, ChevronRight, Users, Edit2, Check, X, Trash2, Archive, Eye } from 'lucide-react';

export default function Groups() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [agents, setAgents] = useState([]);
  const [editName, setEditName] = useState({});
  const [loading, setLoading] = useState(true);
  const [batchLeads, setBatchLeads] = useState({});
  const [expandedBatch, setExpandedBatch] = useState({});

  const load = async () => {
    try {
      const [gr, ag] = await Promise.all([api.get('/api/groups'), api.get('/api/agents')]);
      setGroups(gr.data.groups || []);
      setAgents(ag.data.agents || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggle = (id) => setExpanded(p => ({...p, [id]: !p[id]}));

  const toggleBatch = async (batchId) => {
    if (expandedBatch[batchId]) {
      setExpandedBatch(p => ({...p, [batchId]: false}));
      return;
    }
    try {
      const res = await api.get(`/api/leads?batch_id=${batchId}&limit=500`);
      setBatchLeads(p => ({...p, [batchId]: res.data.leads || []}));
      setExpandedBatch(p => ({...p, [batchId]: true}));
    } catch(e) { console.error(e); }
  };

  const assignBatch = async (batchId, agentId) => {
    if (!agentId) return;
    try {
      await api.put(`/api/groups/batches/${batchId}/assign`, { agent_id: agentId });
      load();
    } catch(e) { alert('Error assigning batch'); }
  };

  const unassignBatch = async (batchId) => {
    try { await api.put(`/api/groups/batches/${batchId}/unassign`); load(); } catch(e) {}
  };

  const saveName = async (groupId) => {
    try {
      await api.put(`/api/groups/${groupId}`, { name: editName[groupId] });
      setEditName(p => ({...p, [groupId]: undefined}));
      load();
    } catch(e) {}
  };

  const archiveGroup = async (groupId, groupName) => {
    if (!window.confirm(`Archive group "${groupName}"? Leads will be kept but group will be hidden.`)) return;
    try { await api.put(`/api/groups/${groupId}/archive`); load(); } catch(e) { alert('Error archiving group'); }
  };

  const deleteGroup = async (groupId, groupName) => {
    if (!window.confirm(`PERMANENTLY delete group "${groupName}" and ALL ${groups.find(g=>g.id===groupId)?.total_leads || ''} leads? This CANNOT be undone.`)) return;
    try { await api.delete(`/api/groups/${groupId}`); load(); } catch(e) { alert('Error deleting group'); }
  };

  if (!['admin','marketing','toplevman'].includes(user?.role)) {
    return <div className="glass rounded-xl p-8 text-center text-dark-400">Access restricted to Admin, Marketing and TopLevMan only.</div>;
  }

  if (loading) return <div className="flex justify-center h-64 items-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div></div>;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-xl font-bold text-white">Lead Groups</h3>
        <p className="text-dark-400 text-sm">Manage uploaded lead sheets and assign batches to executives</p>
      </div>

      {groups.length === 0 && (
        <div className="glass rounded-xl p-8 text-center text-dark-400">No groups yet. Upload a lead file with a group name to get started.</div>
      )}

      {groups.map(group => (
        <div key={group.id} className="glass rounded-xl overflow-hidden">
          <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-dark-700/20" onClick={() => toggle(group.id)}>
            {expanded[group.id] ? <ChevronDown className="w-4 h-4 text-primary-400"/> : <ChevronRight className="w-4 h-4 text-dark-400"/>}
            {editName[group.id] !== undefined ? (
              <div className="flex items-center gap-2 flex-1" onClick={e => e.stopPropagation()}>
                <input value={editName[group.id]} onChange={e => setEditName(p => ({...p, [group.id]: e.target.value}))}
                  className="flex-1 px-3 py-1.5 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500" />
                <button onClick={() => saveName(group.id)} className="p-1.5 bg-green-500/10 text-green-400 rounded"><Check className="w-4 h-4"/></button>
                <button onClick={() => setEditName(p => ({...p, [group.id]: undefined}))} className="p-1.5 bg-dark-700 text-dark-400 rounded"><X className="w-4 h-4"/></button>
              </div>
            ) : (
              <div className="flex-1">
                <span className="text-white font-semibold">{group.name}</span>
                <span className="text-dark-400 text-xs ml-3">{group.filename}</span>
              </div>
            )}
            <div className="flex items-center gap-4 text-sm">
              <span className="text-dark-400">{group.total_leads} leads</span>
              <span className="text-dark-400">{group.batch_count} batches of {group.batch_size}</span>
              <span className="text-green-400">{group.batches?.filter(b => b.status === 'assigned').length || 0} assigned</span>
              <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                <button onClick={() => setEditName(p => ({...p, [group.id]: group.name}))}
                  className="p-1.5 bg-dark-700 hover:bg-dark-600 rounded text-dark-400" title="Rename"><Edit2 className="w-3 h-3"/></button>
                <button onClick={() => archiveGroup(group.id, group.name)}
                  className="p-1.5 bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 rounded" title="Archive group (keep leads)"><Archive className="w-3 h-3"/></button>
                <button onClick={() => deleteGroup(group.id, group.name)}
                  className="p-1.5 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded" title="Delete group and all leads permanently"><Trash2 className="w-3 h-3"/></button>
              </div>
            </div>
          </div>

          {expanded[group.id] && (
            <div className="border-t border-dark-700">
              {group.batches?.map(batch => (
                <div key={batch.id} className="border-b border-dark-700/50 last:border-b-0">
                  <div className="flex items-center justify-between px-6 py-3 hover:bg-dark-800/20">
                    <div className="flex items-center gap-4">
                      <button onClick={() => toggleBatch(batch.id)} className="flex items-center gap-2 text-left">
                        {expandedBatch[batch.id] ? <ChevronDown className="w-3 h-3 text-primary-400"/> : <ChevronRight className="w-3 h-3 text-dark-400"/>}
                        <span className="text-white text-sm font-medium">{batch.batch_name}</span>
                      </button>
                      <span className="text-dark-400 text-xs">{batch.lead_count} leads</span>
                      {batch.status === 'assigned' ? (
                        <span className="px-2 py-0.5 rounded text-xs bg-green-500/10 text-green-400">→ {batch.assigned_agent_name}</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs bg-dark-600 text-dark-400">Unassigned</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <select defaultValue="" onChange={e => assignBatch(batch.id, e.target.value)}
                        className="px-3 py-1.5 bg-dark-800 border border-dark-600 rounded-lg text-white text-xs">
                        <option value="">Assign to...</option>
                        {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                      {batch.status === 'assigned' && (
                        <button onClick={() => unassignBatch(batch.id)} className="px-2 py-1.5 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded text-xs">Unassign</button>
                      )}
                    </div>
                  </div>
                  {expandedBatch[batch.id] && (
                    <div className="px-8 py-2 bg-dark-900/50 space-y-1 max-h-64 overflow-y-auto">
                      {(batchLeads[batch.id] || []).length === 0 ? (
                        <p className="text-dark-400 text-xs py-2">No leads found in this batch</p>
                      ) : (batchLeads[batch.id] || []).map(lead => (
                        <div key={lead.id} className="flex items-center justify-between py-1.5 border-b border-dark-800/50 last:border-0">
                          <span className="text-white text-sm">{lead.name}</span>
                          <div className="flex items-center gap-3 text-xs text-dark-400">
                            <span>{lead.phone}</span>
                            <span className={`px-2 py-0.5 rounded ${lead.status === 'new' ? 'bg-blue-500/10 text-blue-400' : 'bg-dark-600 text-dark-300'}`}>{lead.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
